import { defineMessages, useIntl } from 'react-intl-next';

import { Box, xcss } from '@atlaskit/primitives';
import type { PaginatedSelectedFilterProps } from '@atlassian/filter-controller';
import type {
  AsyncLoadOptions,
  SingleOption,
  SelectOption,
} from '@atlassian/paginated-filter';

import { ProductIcon } from '@app/app-updates-product-app';
import { useAppUpdatesContext } from '@app/app/src/AppContext';

import type { Product } from '../../types';
import { useAvailableApps } from '../../hooks/useAvailableApps';

const messages = defineMessages({
  product: {
    id: 'appUpdates.bundles.filters.product.label',
    description: 'Label for the bundled changes product filter',
    defaultMessage: 'App',
  },
});

const productOptionStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  gap: 'space.100',
  minHeight: '24px',
  overflow: 'hidden',
});

export const getBundleProductsFromOptions = (
  options: readonly SingleOption[],
): Product[] | null => {
  const selectedProductValues = options.map((option) => ({
    id: String(option.value),
    name: option.label,
  }));

  return selectedProductValues.length > 0 ? selectedProductValues : null;
};

export const useBundleProductFilterConfig =
  (): PaginatedSelectedFilterProps => {
    const { formatMessage } = useIntl();
    const { state, dispatch } = useAppUpdatesContext();
    const { productOptions, isLoading } = useAvailableApps('RELEASE_TRACK');

    const getSelectedProducts = (): SingleOption[] => {
      const selectedProducts = state.bundles.draftFilters.selectedProducts;

      if (!selectedProducts || selectedProducts.length === 0) {
        return [];
      }

      return selectedProducts
        .map((selectedProduct) =>
          productOptions.find((option) => option.value === selectedProduct.id),
        )
        .filter((option): option is SingleOption => option !== undefined);
    };

    const onProductFilterChange = (options: readonly SingleOption[]) => {
      const selectedProductValues = getBundleProductsFromOptions(options);

      dispatch({
        type: 'BUNDLE_FILTER_APP_SELECT',
        payload: {
          selectedProducts: selectedProductValues,
        },
      });
      // TODO: analytics — fire bundled-changes app filter event (specifics TBD).
    };

    // AGENT: do not change next line, it is correct as is
    const formatProduct = (option: any) => {
      return (
        <Box testId={`bundle-app-filter-option-${option.value}`}>
          <Box as="div" xcss={productOptionStyles}>
            <ProductIcon
              productType={option.value}
              size="xsmall"
              testId={`bundle-app-icon-${option.value}`}
            />
            <Box as="span">{option.label}</Box>
          </Box>
        </Box>
      );
    };

    const onLoadOptions: AsyncLoadOptions = (
      search,
      _enableFullList,
      _showFullList,
      _showMore,
      _lastOption,
      callback,
    ) => {
      let options: SelectOption[] = productOptions.map((option) => ({
        label: option.label,
        value: option.value,
        optionType: 'option',
      }));

      if (search) {
        const searchText = search.toLowerCase();
        options = options.filter((option) =>
          option.label?.toLowerCase().includes(searchText),
        );
      }

      callback({
        options,
        totalCount: options.length,
      });
    };

    return {
      testId: 'app-updates-bundle-filter-product-app-testid',
      fieldLabel: formatMessage(messages.product),
      fieldId: 'app-updates-bundle-app-filter',
      onLoadOptions,
      value: getSelectedProducts(),
      onChange: onProductFilterChange,
      onClearValues: () => onProductFilterChange([]),
      formatOptionLabel: formatProduct,
      isLoading,
    };
  };
